// Shared "finalize the order" step used by both the cash and card (PIN-pad) tenders.
// Recomputes the pricing pipeline over a clone of the cart (so the authoritative wire values are
// fresh), builds the saveOrderPos payload, and — LIKE THE ANDROID POS — saves it LOCALLY (offline
// queue) and returns IMMEDIATELY so the receipt shows/prints without waiting on the server; the
// background worker POSTs saveOrderPos and dequeues on confirmation (Android insertSaveOrderData →
// OfflineManager.startOfflineOperation()). Direct awaited save remains only as the fallback when
// IndexedDB is unavailable.

import { recalculate, buildSavePayload, formatOrderDateTime } from "../engine";
import { buildRecalcInput } from "../../../Redux/features/WebRegister/cartSlice";
import { getMixMatches } from "./queries";
import { deziSaveOrderFields } from "../engine/dezi";
import { phpSaveOrder, auth } from "./api";
import { enqueueOrder, removeOfflineOrder, isSaved, isOrderAlreadyExists, kickFlush } from "./offlineQueue";
import { getLoyaltyConfig } from "./tenderData";
import { getEnableOrderNumber } from "./registerSettings";

// Cap the payload-only lookups so a cold cache can never hold the receipt hostage (Android reads
// enableOrderNumber from its LOCAL store-details table — instant; the web memoizes after a prefetch
// at register open, so this race only bites on a cold+slow network and resolves to the safe "0").
const withTimeout = (p, ms, fallback) =>
  Promise.race([p, new Promise((resolve) => setTimeout(() => resolve(fallback), ms))]);

const cloneLines = (lines) =>
  lines.map((l) => ({ ...l, model: { ...(l.model || {}) }, taxList: (l.taxList || []).map((t) => ({ ...t })) }));

/**
 * Finalize the order from the full cart state (reductions + payment tenders live on the slice).
 * @param {Object} p
 * @param {Object} p.cart        webRegisterCart slice state
 * @param {number} [p.cashPaid]  cash tendered on the final single cash tender (for change_due)
 * @param {string} [p.orderId]   client order id (shared with the terminal instruction(s) for card)
 * @returns {Promise<Object>} saveOrderPos response { status, message, order_id, id }
 */
export async function submitRegisterOrder({ cart, cashPaid = 0, orderId } = {}) {
  const clone = cloneLines(cart.lines);
  const giftCard = (cart.giftCards || []).reduce((s, g) => s + Number(g.amountUsed || 0), 0);
  // SAME inputs as the cart slice (dezi result, OTD, coupon split, dual price) — see buildRecalcInput.
  const recalc = recalculate(buildRecalcInput(cart, clone, giftCard));
  const a = auth();
  // Loyalty config for EARN accrual (lp_earn_*) + the store enable_order_number flag. Both best-effort
  // (fetched in parallel — no added latency): loyalty null → no earn sent; flag "0" → server leaves the
  // order unnumbered. enable_order_number is the web mirror of Android getStoreDetailsData().enableOrderNumber.
  const [loyaltyCfg, enableOrderNumber] = await Promise.all([
    getLoyaltyConfig().catch(() => null), // IDB meta — instant
    withTimeout(getEnableOrderNumber().catch(() => "0"), 2000, "0"), // memoized after register-open prefetch
  ]);
  // Current Mix & Match promotion codes (IDB, refreshed by the sync poll) — best-effort, never blocks a sale.
  let mixMatchPromoById = null;
  if ((cart.lines || []).some((l) => l.isBulkPriceApplied)) {
    try {
      const deals = await withTimeout(getMixMatches(), 1500, []);
      mixMatchPromoById = new Map((deals || []).map((d) => [String(d.id), d.promotion_code == null ? "" : String(d.promotion_code)]));
    } catch {
      mixMatchPromoById = null;
    }
  }
  // mKonnekt sale-time capture: the quote that belongs to the result the slice APPLIED (all "" when
  // Dezi is off / no lookup). It is part of the payload, so a queued offline order carries it too.
  // Only when that quote was asked for THIS cart and customer phone — after a tender-wait timeout the
  // slice can still hold an older basket's result, and that lookup does not describe this sale.
  const mk = deziSaveOrderFields(cart.deziDiscounts, {
    lines: cart.lines,
    mobileNumber: (cart.customer && (cart.customer.phone || cart.customer.mobile)) || "",
  });
  const payload = buildSavePayload({
    cart: clone,
    recalc,
    taxResult: recalc.tax,
    couponTotal: cart.couponTotal,
    customTotal: cart.customTotal,
    couponCode: cart.coupon ? cart.coupon.code : "",
    couponId: cart.coupon ? cart.coupon.id : "",
    isOutdoor: cart.isOutdoor,
    isEighteenPlus: cart.isEighteenPlus,
    cashPaid,
    orderId,
    reductions: {
      giftCards: cart.giftCards,
      loyaltyAmount: cart.loyaltyAmount,
      loyaltyPointsSpent: cart.loyaltyPointsSpent,
      storeCreditAmount: cart.storeCreditAmount,
    },
    tenders: cart.payments,
    ctx: {
      merchantId: a.merchantId,
      employeeId: a.employeeId,
      employeeName: a.employeeName || "", // Android puts the cashier name in the loyalty JSON
      groupId: a.groupId,
      customerId: cart.customer ? cart.customer.customer_id || cart.customer.id || "" : "",
      orderDateTime: formatOrderDateTime(new Date()), // bare local "yyyy-MM-dd HH:mm:ss" (NOT ISO — server strtotime fails on 'T'/'Z'/millis → 1969)
      orderMethod: "pickup",
      enableOrderNumber, // store flag → saveOrderPos numbers the order when "1" (order_number stays "0")
      idNumber: cart.idNumber || "",
      idCardType: cart.idCardType || "",
      dob: cart.idDob || "", // Android idCardDateOfBirth (age-verify prompts / ID scan)
      idCardExDate: cart.idCardExDate || "", // Android idCardExpiryDate (expiry prompt)
      dualPrice: cart.dualPrice || null, // QV-4241 store surcharge mode for the payload conversion
      mixMatchPromoById, // Android 77992678da — current Mix & Match promotion codes by deal id
      mkonnektDiscountResponse: mk.mkonnekt_discount_response,
      mkonnektMobileNumber: mk.mkonnekt_mobile_number,
      mkonnektRcn: mk.mkonnekt_rcn,
      // (no order-level `coupon` ctx: QV Altria moved the funded-discount flag onto SPECIFIC item
      // coupons, so altria_discount_desc.coupon now reads the line's own item coupon — Android
      // ee2ae0d512 likewise deleted getAppliedCouponModel.)
      loyalty: loyaltyCfg
        ? {
            enabled: loyaltyCfg.enabled,
            pointsPerDollar: loyaltyCfg.pointsPerDollar,
            bonusPointsPerDollar: loyaltyCfg.bonusPointsPerDollar, // active promotion bonus points
            dollarPerPoint: loyaltyCfg.dollarPerPoint,
          }
        : null,
    },
  });

  // LOCAL-FIRST (Android parity): persist the order to the offline queue — that IS the save from the
  // cashier's point of view. Return immediately so the receipt shows/prints without waiting on
  // saveOrderPos; kick the background worker (fire-and-forget), which POSTs the order and dequeues
  // only when the server confirms (status:true, or the "already exists" duplicate answer).
  const oid = payload.order_id; // the actual id used (buildSavePayload generates one if none passed)
  const kind = (cart.payments || []).some((p) => String(p.payType).toLowerCase() !== "cash") ? "card" : "cash";
  try {
    await enqueueOrder({ orderId: oid, payload, kind });
    kickFlush(); // background sync starts NOW (single-flight; NOT awaited)
    return { status: true, queued: true, order_id: oid, payload };
  } catch {
    /* IDB unavailable (private mode / storage broken) — fall back to the awaited direct save */
  }

  let res;
  try {
    res = await phpSaveOrder(payload);
  } catch (e) {
    // No local queue AND no network — surface the failure; the tender may need reconciliation.
    return { status: false, message: e && e.message ? e.message : String(e) };
  }
  if (isSaved(res) || isOrderAlreadyExists(res)) {
    try {
      await removeOfflineOrder(oid);
    } catch {
      /* ignore */
    }
    // "Order already exists." = an earlier attempt (e.g. a retry after a lost response) committed it
    // server-side — surface it as a SUCCESS so the sale completes instead of erroring/looping.
    return isSaved(res) ? { ...res, payload } : { ...res, status: true, duplicate: true, payload };
  }
  return res;
}
